import React, { forwardRef } from 'react';
import styled, { css } from 'styled-components';
import PropTypes from 'prop-types';
import { easing } from './lib/easing';
import Icon from './Icon';
import { Link } from './Link';

const Primary = styled.button`
  align-items: center;
  background: var(--primary-grad);
  border-radius: 6px;
  border: none;
  color: white;
  display: flex;
  flex-shrink: 0;
  font-size: var(--regular);
  font-weight: var(--semi-bold);
  height: 40px;
  justify-content: center;
  line-height: 1.5;
  padding: 8px 16px;
  text-align: center;
  text-decoration: none;
  transition: ${easing('background', 'color', 'box-shadow')};

  &:hover:not([disabled]) {
    box-shadow: var(--button-elevation);
  }

  &[disabled] {
    background: var(--disabled);
    color: var(--white);

    box-shadow: none;
    transition: background 0ms;
  }

  ${Icon} {
    color: inherit;
    font-size: var(--regular);
    margin-right: 1em;
  }
`;

const Secondary = styled(Primary)`
  background: var(--white);
  border: 1px solid var(--sharkbait-ooh-la-la);
  color: var(--text-primary);
  padding: 7px 16px;

  &:hover:not([disabled]) {
    color: var(--oh-hi-dark);
  }

  &[disabled] {
    background: var(--white);
    color: var(--disabled-dark);
  }
`;

const Positive = styled(Primary)`
  background: var(--safety-grad);
`;

const Negative = styled(Primary)`
  background: var(--danger-grad);
`;

export const ButtonLink = styled(Link).attrs({
  as: 'button',
})`
  background: none;
  border: none;
`;

const UnstyledButton = forwardRef(
  ({ kind, type = 'button', ...props }, ref) => {
    switch (kind) {
      case 'secondary':
        return <Secondary ref={ref} type={type} {...props} />;
      case 'negative':
        return <Negative ref={ref} type={type} {...props} />;
      case 'positive':
        return <Positive ref={ref} type={type} {...props} />;
      case 'primary':
      default:
        return <Primary ref={ref} type={type} {...props} />;
    }
  }
);

UnstyledButton.propTypes = {
  kind: PropTypes.oneOf(['primary', 'secondary', 'positive', 'negative']),
  type: PropTypes.string,
};

const Button = styled(UnstyledButton)``;

export default Button;

export const SquareButton = styled(Button).attrs(({ icon }) => ({
  children: <Icon icon={icon} />,
}))`
  padding: 8px;
  width: 40px;
  height: 40px;

  ${Icon} {
    margin-right: 0;

    ${({ kind }) =>
      kind === 'secondary' &&
      `
      color: var(--icon);
    `}
  }
`;

const sharedFocusStyles = css`
  content: '';
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  border: 2px solid var(--pleather);
`;

export const SplitButton = styled.div`
  display: flex;
  width: fit-content;
  position: relative;
  background: var(--primary-grad);
  border-radius: 8px;
  ${props => props.kind === 'positive' && 'background: var(--safety-grad);'}
  ${props =>
    props.disabled &&
    `background: var(--disabled); 
    ${Button} {
      pointer-events: none;
      cursor: not-allowed;
      :hover {
        box-shadow: none;
      }
    }
    `}

  ${Button} {
    border: none;
    border-radius: 0;
    outline: none;
    background: none;
    position: relative;
  }

  ${Button}:first-of-type {
    :focus-visible {
      :before {
        ${sharedFocusStyles}
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
      }
    }
  }

  ${Button}:not(:first-of-type) {
    border-top-left-radius: 0px;
    border-bottom-left-radius: 0px;
    border-left: 1px solid rgba(255, 255, 255, 0.3);
    padding: 18px 11px;

    ${Icon} {
      margin: 0;
      font-size: var(--xx-small);
    }
    :focus-visible {
      :before {
        ${sharedFocusStyles}
        border-top-left-radius: 0px;
        border-bottom-left-radius: 0px;
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
      }
    }
  }
`;

SplitButton.displayName = 'SplitButton';

export const ButtonGroup = styled.div`
  align-items: stretch;
  display: flex;

  > ${Button}, > ${SplitButton} {
    margin-right: 12px;
  }

  ${({ right }) =>
    right &&
    `
      flex-direction: row-reverse;

      > ${Button}, > ${SplitButton} {
        margin-left: 12px;
        margin-right: 0;
      }
    `}
`;
